import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { api, ApiError } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { useSession } from '../../shell/session-context';
import { usePlan } from '../../shell/plan-context';
import { useResource } from '../../shell/use-resource';
import { ConfirmWithReason } from '../../shell/confirm-with-reason';
import { HomeChecklist, HomeDetailsLine } from './home-checklist';
import { leastComplete } from '../business/listing-extras-model';
import { HomeKpis } from './home-kpis';
import { HomeAttention } from './home-attention';
import { HomeSchedule } from './home-schedule';
import { HomeBusinessCards } from './home-business-cards';
import { HomeChart } from './home-chart';
import { HomeFreeCard, HomeFreeNotice } from './home-free-card';
import type { FreeGateView } from './home-free-card';
import { HomeShareCard } from './home-share-card';
import {
  PERIOD_DAYS,
  addDays,
  addMonths,
  attentionItems,
  branchLabel,
  businessName,
  byStart,
  greetingPart,
  kpisFromBookings,
  kpisFromInsights,
  lastDays,
  localDay,
  ratingOf,
  seriesFromBookings,
  seriesFromInsights,
} from './home-model';
import type { Period } from './home-model';
import type {
  CatalogItem,
  Insights,
  OnboardingChecklist,
  MerchantBooking,
  MerchantBookingList,
  MerchantOffer,
  Pricing,
} from '../../mock/seeds/schema-types';
import './home.css';

type BookingQuery = { business_id?: string; from?: string; to?: string; tab?: 'payment_check' | 'hidden' | 'upcoming' | 'all' };

const GREETING: Record<ReturnType<typeof greetingPart>, MessageKey> = {
  morning: 'dc71.greeting.morning',
  afternoon: 'dc71.greeting.afternoon',
  evening: 'dc71.greeting.evening',
};
// How far ahead the "Coming up" rows look.
const UPCOMING_DAYS = 30;
const DISMISS_KEY = 'bd.merchant.home.checklist-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/** Insights for the whole account; `null` when the plan lacks the entitlement (403). */
async function loadInsights(range: Period, signal: AbortSignal): Promise<Insights | null> {
  try {
    return await api.GET('/merchant/insights', { params: { query: { business_id: 'all', range } }, signal });
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) return null;
    throw err;
  }
}

// Home (PRD §7.3.3 row 1, FR-B7; design M:350-847). The whole account at a glance —
// the Home reads every business (`business_id=all`, API-95/API-123); the per-business
// tabs on the schedule and chart narrow it. Sections top to bottom: greeting, Get set
// up (API-104), At a glance with the period switcher, Needs your attention, the Free
// plan notice, Today's schedule, My Business + the 30-day chart, the Free usage card,
// and Your booking link. When the plan has no insights entitlement the KPI cards and
// chart are computed from the bookings list instead.
//
// A team member's Home (API-16; design persona `xh-team`): the calendar reads only —
// no `business_id` (the server scopes a staff token to its own businesses, and `all`
// is owner-only), KPIs and the chart from those rows, no Get set up, insights, plan,
// payment-proof queue, per-business catalog/offers, My Business cards or booking link
// (each is a merchant-only read), and no New booking (the walk-in wizard needs the
// merchant-only catalog). Business names come from the session's `teamBusinesses`.
export function HomeScreen() {
  const { t, lang } = useLanguage();
  const { user, businesses, epoch, isStaff, teamBusinesses } = useSession();
  const { entitlements, subscription } = usePlan();
  const [period, setPeriod] = useState<Period>('30d');
  const [dayOffset, setDayOffset] = useState(0);
  const [schedTab, setSchedTab] = useState('all');
  const [dismissed, setDismissed] = useState(readDismissed);
  const [askDismiss, setAskDismiss] = useState(false);
  const navigate = useNavigate();
  // "Now" for this render generation; a business switch or a reload re-reads it.
  const now = useMemo(() => new Date(), [epoch]);
  const today = localDay(now);
  const deps = [epoch, isStaff];
  // Every owner read asks for the whole account; a staff token is scoped by the server.
  const everyBusiness: BookingQuery = isStaff ? {} : { business_id: 'all' };

  const list = (query: BookingQuery, signal: AbortSignal) =>
    api.GET('/merchant/bookings', { params: { query }, signal }) as Promise<MerchantBookingList>;

  const onboarding = useResource<OnboardingChecklist | undefined>(
    async (signal) => {
      if (isStaff) return undefined; // merchant-only (API-104)
      try {
        return await api.GET('/merchant/onboarding', { signal });
      } catch {
        return undefined;
      }
    },
    deps,
  );
  const pricing = useResource<Pricing | undefined>(
    async (signal) => {
      if (isStaff) return undefined; // only the owner's checklist names a plan
      try {
        return await api.GET('/pricing', { signal, lang });
      } catch {
        return undefined;
      }
    },
    [lang, isStaff],
  );

  // Insights are merchant-only (API-16): a staff session goes straight to the bookings
  // fallback, as a plan without the entitlement does.
  const periodInsights = useResource<Insights | null>(
    (signal) => (isStaff ? Promise.resolve(null) : loadInsights(period, signal)),
    [period, ...deps],
  );
  const chartInsights = useResource<Insights | null>(
    (signal) => (isStaff ? Promise.resolve(null) : loadInsights('30d', signal)),
    deps,
  );
  const insightsLocked = periodInsights.data === null || chartInsights.data === null;

  // Fallback reads when insights are locked: the period window and the last 30 days.
  const fallback = useResource<{ period: MerchantBooking[]; month: MerchantBooking[] } | null>(
    async (signal) => {
      if (!insightsLocked) return null;
      const month = (await list({ ...everyBusiness, from: localDay(addDays(now, -29)), to: today }, signal)).items;
      const from = localDay(addDays(now, period === '1d' ? -1 : -(PERIOD_DAYS[period] - 1)));
      const to = period === '1d' ? from : today;
      const rows = period === '30d' ? month : (await list({ ...everyBusiness, from, to }, signal)).items;
      return { period: rows, month };
    },
    [insightsLocked, period, ...deps],
  );

  const todayRows = useResource<MerchantBooking[]>(
    async (signal) => (await list({ ...everyBusiness, from: today, to: today }, signal)).items,
    deps,
  );
  const dayKey = localDay(addDays(now, dayOffset));
  const dayRows = useResource<MerchantBooking[]>(
    async (signal) => (dayOffset === 0 ? [] : (await list({ ...everyBusiness, from: dayKey, to: dayKey }, signal)).items),
    [dayKey, ...deps],
  );
  // The next live bookings after today (the "Coming up" rows under an empty today).
  const upcomingRows = useResource<MerchantBooking[]>(
    async (signal) =>
      (await list({ ...everyBusiness, tab: 'upcoming', from: localDay(addDays(now, 1)), to: localDay(addDays(now, UPCOMING_DAYS)) }, signal)).items
        .slice()
        .sort(byStart),
    deps,
  );
  // Verifying a payment is merchant-only (API-16): a team member gets no proof queue.
  const proofQueue = useResource<MerchantBooking[]>(
    async (signal) => (isStaff ? [] : (await list({ business_id: 'all', tab: 'payment_check' }, signal)).items),
    deps,
  );
  const hiddenRows = useResource<MerchantBooking[]>(
    async (signal) => (isStaff ? [] : (await list({ business_id: 'all', tab: 'hidden' }, signal)).items),
    deps,
  );
  const ids = businesses.map((b) => b.id).join(',');
  const perBusiness = useResource<{ services: CatalogItem[]; offers: MerchantOffer[] }>(
    async (signal) => {
      const all = await Promise.all(
        businesses.map(async (b) => {
          const [cat, off] = await Promise.all([
            api.GET('/merchant/businesses/{id}/catalog', { params: { path: { id: b.id } }, signal }).catch(() => ({ items: [] as CatalogItem[] })),
            api.GET('/merchant/businesses/{id}/offers', { params: { path: { id: b.id } }, signal }).catch(() => ({ items: [] as MerchantOffer[] })),
          ]);
          return { services: cat.items ?? [], offers: (off as { items?: MerchantOffer[] }).items ?? [] };
        }),
      );
      return { services: all.flatMap((x) => x.services), offers: all.flatMap((x) => x.offers) };
    },
    [ids, ...deps],
  );

  // ---- derived ----
  const reviews = ratingOf(businesses);
  const figures = periodInsights.data
    ? kpisFromInsights(periodInsights.data, reviews.reviews)
    : fallback.data
      ? kpisFromBookings(fallback.data.period, reviews.rating, reviews.reviews)
      : null;
  const monthDays = lastDays(now, 30);
  // The businesses this session names: the owner's rows, or a team member's (session).
  const named: { id: string; name: string; name_bn?: string }[] = isStaff ? teamBusinesses : businesses;
  const series = chartInsights.data
    ? seriesFromInsights(chartInsights.data)
    : fallback.data
      ? seriesFromBookings(fallback.data.month, monthDays, named.map((b) => b.id))
      : [];
  const chartTrend = chartInsights.data ? kpisFromInsights(chartInsights.data, reviews.reviews).trends.bookings : null;

  const todays = todayRows.data ?? [];
  const pending = (proofQueue.data ?? []).length;
  const attention = attentionItems({
    proofQueue: proofQueue.data ?? [],
    services: perBusiness.data?.services ?? [],
    offers: perBusiness.data?.offers ?? [],
    businesses,
    now,
  });
  const namesForUi = named.map((b) => ({ id: b.id, name: businessName(b, lang) }));
  const nameOf = (id: string) => branchLabel(namesForUi.find((b) => b.id === id)?.name ?? '');

  // The booked service in the UI language: the catalog row's name (bn when chosen),
  // else the booking's own snapshot (a removed or unknown service).
  const serviceName = (b: MerchantBooking) => {
    const line = b.items?.[0];
    const item = line?.catalog_item_id ? perBusiness.data?.services.find((c) => c.id === line.catalog_item_id) : undefined;
    return item ? (lang === 'bn' && item.name_bn ? item.name_bn : item.name_en) : (line?.name_snapshot ?? '—');
  };

  const steps = onboarding.data?.steps ?? [];
  const showChecklist = steps.length > 0 && !steps.every((s) => s.done) && !dismissed;
  // DR-87: the listing with the fewest details on file, for the "Complete your listing" line.
  const incomplete = isStaff ? null : leastComplete(businesses);
  const lockedPlan = (pricing.data?.plans ?? [])
    .filter((p) => p.entitlements?.payments === true)
    .sort((a, b) => (a.tier_order ?? 0) - (b.tier_order ?? 0))[0];
  const lockedPlanName = lockedPlan ? (lang === 'bn' ? lockedPlan.name_bn : lockedPlan.name_en) : '';

  // Free gate (DR-31): only for a plan with a monthly booking cap.
  const cap = entitlements?.bookings_per_month;
  const gate: FreeGateView | null =
    typeof cap === 'number'
      ? {
          cap,
          promoMonths: Number(entitlements?.bookings_uncapped_months ?? 0),
          promoEnds:
            subscription?.created_at && Number(entitlements?.bookings_uncapped_months ?? 0) > 0
              ? addMonths(subscription.created_at, Number(entitlements?.bookings_uncapped_months))
              : null,
          usedThisMonth: subscription?.usage?.bookings_this_month ?? 0,
          hidden: hiddenRows.data ?? [],
        }
      : null;

  const bizCap = entitlements?.businesses;
  const ownerName = user?.name ?? '';

  return (
    <div className="hm">
      <div className="hm-hello">
        <h1 className="hm-hello__title">{formatMessage(lang, GREETING[greetingPart(now.getHours())], { name: ownerName })}</h1>
        <p className="hm-hello__sub">{formatMessage(lang, 'dc71.subtitle', { n: todays.length, p: pending })}</p>
      </div>

      {showChecklist && <HomeChecklist steps={steps} lockedPlanName={lockedPlanName} onDismiss={() => setAskDismiss(true)} incomplete={incomplete} />}
      {!showChecklist && incomplete && (
        <section className="hm-card" aria-label={formatMessage(lang, 'lr.merchant.details.line', { name: incomplete.name, n: incomplete.details?.score ?? 0 })}>
          <HomeDetailsLine business={incomplete} />
        </section>
      )}

      <HomeKpis period={period} onPeriod={setPeriod} figures={figures} />

      <HomeAttention items={attention} nameOf={nameOf} />

      {gate && <HomeFreeNotice gate={gate} now={now} />}

      <HomeSchedule
        rows={dayOffset === 0 ? todays : (dayRows.data ?? [])}
        businesses={namesForUi}
        tab={schedTab}
        onTab={setSchedTab}
        dayOffset={dayOffset}
        onDay={setDayOffset}
        now={now}
        onNewBooking={isStaff ? undefined : () => navigate('/bookings?new=1')}
        serviceName={serviceName}
        upcoming={upcomingRows.data ?? []}
      />

      <div className="hm-duo">
        {!isStaff && (
          <HomeBusinessCards businesses={businesses} today={todays} cap={typeof bizCap === 'number' ? bizCap : null} onPick={setSchedTab} serviceName={serviceName} />
        )}
        <HomeChart series={series} businesses={namesForUi} trend={chartTrend} insightsLocked={insightsLocked} showDetails={!isStaff} />
      </div>

      {gate && <HomeFreeCard gate={gate} now={now} />}

      {!isStaff && <HomeShareCard businesses={businesses} />}

      <ConfirmWithReason
        open={askDismiss}
        title={t('dc71.checklist.dismiss_title')}
        body={t('dc71.checklist.dismiss_body')}
        showNote={false}
        tone="primary"
        confirmLabel={t('dc71.checklist.dismiss')}
        onCancel={() => setAskDismiss(false)}
        onConfirm={() => {
          try {
            localStorage.setItem(DISMISS_KEY, '1');
          } catch {
            /* storage blocked — dismissed for this visit */
          }
          setDismissed(true);
          setAskDismiss(false);
        }}
      />
    </div>
  );
}
